import lodash from 'lodash';
import Vue from 'vue';

const state = () => ({
  forms: {},
  internalForms: {},
  responses: {},
  validations: {},
  loading: {},
  currentSteps: {},
  defaultFormData: {},
  formDialogs: {},
  initialLoading: false,
  dialogTitle: '',
  dialogButtons: [],
  headerType: 'navigation',
  dialogLeftHeaderIcon: '',
  dialogLeftHeaderIconType: 'static',
  dialogLeftHeaderIconComponent: '',
  attachments: {},
  returnData: {}
});

const getters = {
  forms(state) {
    return state.forms;
  },
  internalForms(state) {
    return state.internalForms;
  },
  responses(state) {
    return state.responses;
  },
  validations(state) {
    return state.validations;
  },
  loading(state) {
    return state.loading;
  },
  currentSteps(state) {
    return state.currentSteps;
  },
  defaultFormData(state) {
    return state.defaultFormData;
  },
  initialLoading(state) {
    return state.initialLoading;
  },
  formDialogs(state) {
    return state.formDialogs;
  },
  dialogTitle(state) {
    return state.dialogTitle;
  },
  dialogButtons(state) {
    return state.dialogButtons;
  },
  headerType(state) {
    return state.headerType;
  },
  dialogLeftHeaderIcon(state) {
    return state.dialogLeftHeaderIcon;
  },
  dialogLeftHeaderIconType(state) {
    return state.dialogLeftHeaderIconType;
  },
  dialogLeftHeaderIconComponent(state) {
    return state.dialogLeftHeaderIconComponent;
  },
  attachments(state) {
    return state.attachments;
  },
  returnData(state) {
    return state.returnData;
  }
};

const mutations = {
  updateLoading(state, payload) {
    Vue.set(state.loading, payload.formName, payload.value);
  },
  updateForms(state, payload) {
    Vue.set(state.forms, payload.formName, payload.value);
  },
  updateInternalForms(state, payload) {
    Vue.set(state.internalForms, payload.formName, payload.value);
  },
  updateCurrentStep(state, payload) {
    if (state.forms[payload.formName]?.formType !== 'static') {
      if (state.currentSteps[payload.formName] < payload.step) {
        state.forms[payload.formName].formSteps?.forEach(step => {
          step.formInputs.forEach(input => {
            this.$eventHub.$emit(`resetValidationInput_${input.id}_${payload.step - 1}`);
          });
        });
      }
    }

    Vue.set(state.currentSteps, payload.formName, payload.step);
  },
  updateCurrentSteps(state, payload) {
    Vue.set(state.currentSteps, payload.formName, payload.value);
  },
  updateResponse(state, payload) {
    Vue.set(state.responses[payload.formName], payload.id, payload.value);
  },
  updateResponses(state, payload) {
    Vue.set(state.responses, payload.formName, payload.value);
  },
  resetResponses(state, payload) {
    Vue.set(state.responses, payload, {});
  },
  updateValidation(state, payload) {
    Vue.set(state.validations[payload.formName], payload.id, payload.value);
  },
  updateValidations(state, payload) {
    Vue.set(state.validations, payload.formName, payload.value);
  },
  resetValidations(state, payload) {
    Vue.set(state.validations, payload, {});
  },
  updateFormDialogs(state, payload) {
    Vue.set(state.formDialogs, payload.formName, payload.value);
  },
  updateDefaultFormData(state, payload) {
    Vue.set(state.defaultFormData, payload.formName, payload.value);
  },
  resetAttachments(state, payload) {
    Vue.set(state.attachments, payload, {});
  },
  updateAttachments(state, payload) {
    Vue.set(state.attachments[payload.formName], payload.id, payload.value);
  },
  setDefaultFormData(state, payload) {
    const responsesTmp = lodash.cloneDeep(state.responses);
    if (state.defaultFormData[payload] && state.defaultFormData[payload].length) {
      state.defaultFormData[payload].forEach(val => {
        if (responsesTmp[payload][val.formInputId]) {
          // if multiple values add to array, or make array from string and add to array
          if (Array.isArray(responsesTmp[payload][val.formInputId])) {
            responsesTmp[payload][val.formInputId].push(val.value);
          } else {
            responsesTmp[payload][val.formInputId] = [
              responsesTmp[payload][val.formInputId],
              val.value
            ];
          }
        } else {
          responsesTmp[payload][val.formInputId] = val.value;
        }
      });
    }
    state.forms[payload]?.formSteps?.forEach(step => {
      step.formInputs.forEach(input => {
        if (input.defaultValue && !responsesTmp[payload][input.id]) {
          responsesTmp[payload][input.id] = input.defaultValue;
        }
      });
    });
    state.responses = responsesTmp;
  },
  resetStore(state) {
    state.forms = {};
    state.responses = {};
    state.validations = {};
    state.loading = {};
    state.currentSteps = {};
    state.defaultFormData = {};
    state.formDialogs = {};
  },
  updateInitialLoading(state, payload) {
    state.initialLoading = payload;
  },
  updateDialogTitle(state, payload) {
    state.dialogTitle = payload;
  },
  updateDialogButtons(state, payload) {
    state.dialogButtons = payload;
  },
  updateHeaderType(state, payload) {
    state.headerType = payload;
  },
  updateDialogLeftHeaderIcon(state, payload) {
    state.dialogLeftHeaderIcon = payload;
  },
  setDialogLeftHeaderIconType(state, payload) {
    state.dialogLeftHeaderIconType = payload;
  },
  setDialogLeftHeaderIconComponent(state, payload) {
    state.dialogLeftHeaderIconComponent = payload;
  },
  setReturnData(state, payload) {
    state.returnData = payload;
  }
};

const actions = {
  setForm({ state, commit }, payload) {
    commit('updateForms', {
      formName: payload.formName,
      value: payload.data
    });

    const internalFormsTmp = [];
    if (payload.stepsBefore && payload.stepsBefore.length) {
      payload.stepsBefore.forEach(step => {
        let internalStep = {
          type: 'static',
          nextAction: 'nextStep',
          settings: { ...step }
        };

        if (step.dialogDataStore) {
          internalStep.dialogDataStore = step.dialogDataStore;
          delete step.dialogDataStore;
        }

        if (step.phoneVerificationInput) {
          internalStep.phoneVerificationInput = step.phoneVerificationInput;
          delete step.phoneVerificationInput;
        }

        internalStep.settings = { ...step };
        internalFormsTmp.push(internalStep);
      });
    }
    if (payload.data.formSteps && payload.data.formSteps.length) {
      payload.data.formSteps.forEach(step => {
        const phoneVerificationInput = step.formInputs.filter(
          input => input.systemProperty === 'webuser.phone_with_validation'
        );
        internalFormsTmp.push({
          type: 'dynamic',
          nextAction: 'validate',
          settings: { ...step },
          phoneVerificationInput: phoneVerificationInput.length ? phoneVerificationInput : null
        });
      });
    }
    if (payload.stepsAfter && payload.stepsAfter.length) {
      payload.stepsAfter.forEach(step => {
        internalFormsTmp.push({
          type: 'static',
          nextAction: 'nextStep',
          settings: { ...step }
        });
      });
    }
    commit('updateInternalForms', {
      formName: payload.formName,
      value: internalFormsTmp
    });

    if (!payload.preserveStep) {
      commit('updateResponses', {
        formName: payload.formName,
        value: {}
      });

      commit('updateValidations', {
        formName: payload.formName,
        value: {}
      });

      commit('updateCurrentSteps', {
        formName: payload.formName,
        value: 1
      });

      commit('resetAttachments', payload.formName);
    }

    if (state.formDialogs[payload.formName] === undefined) {
      // only update to default if value is not set (means that form will not be closed if was open during loading)
      commit('updateFormDialogs', {
        formName: payload.formName,
        value: payload.updateRouteOnCreate
      });
    }
  },

  async getFormData({ state, commit, dispatch }, payload) {
    if (state.forms[payload.formName] !== undefined) {
      return;
    }
    commit('updateLoading', {
      formName: payload.formName,
      value: true
    });
    return this.$http
      .get(`/v3/fe/forms/${payload.formName}?region=${payload.region}`)
      .then(data => {
        dispatch('setForm', {
          formName: payload.formName,
          data: data,
          stepsBefore: payload.stepsBefore,
          updateRouteOnCreate: payload.updateRouteOnCreate
        });

        if (payload.loadDefaultData) {
          dispatch('getDefaultFormData', payload.formName);
        } else {
          commit('resetResponses', payload.formName);
          commit('resetValidations', payload.formName);
          commit('resetAttachments', payload.formName);
          commit('updateLoading', {
            formName: payload.formName,
            value: false
          });
        }
      })
      .catch(err => {
        commit('updateLoading', {
          formName: payload.formName,
          value: false
        });

        dispatch('errorModal/handleError', { error: err, code: '0x00000055' }, { root: true });
      });
  },

  getDefaultFormData({ commit, dispatch, state }, payload) {
    if (!state.forms[payload]) {
      return;
    }
    commit('updateLoading', {
      formName: payload,
      value: true
    });
    this.$http
      .get(`/v3/fe/forms/${payload}/template/${state.forms[payload].id}`)
      .then(data => {
        commit('updateDefaultFormData', {
          formName: payload,
          value: data.formItems
        });
      })
      .finally(() => {
        dispatch('resetForm', payload);
        dispatch('updateCurrentStep', { formName: payload, step: 1 });
      });
  },

  getAllDefaultFormData({ dispatch, state }) {
    if (state.forms && Object.keys(state.forms) === 0) {
      return;
    }
    Object.entries(state.forms).forEach(([key]) => {
      if (state.forms[key]?.formType !== 'static') {
        dispatch('getDefaultFormData', key);
      }
    });
  },

  updateCurrentStep({ commit }, payload) {
    commit('updateCurrentStep', payload);
  },
  updateInitialLoading({ commit }, payload) {
    commit('updateInitialLoading', payload);
  },
  updateResponse({ commit }, payload) {
    commit('updateResponse', payload);
  },
  updateValidation({ commit }, payload) {
    commit('updateValidation', payload);
  },
  updateFormDialogs({ commit }, payload) {
    commit('updateFormDialogs', payload);
  },
  updateDialogTitle({ commit }, payload) {
    commit('updateDialogTitle', payload);
  },
  updateDialogButtons({ commit }, payload) {
    commit('updateDialogButtons', payload);
  },
  updateHeaderType({ commit }, payload) {
    commit('updateHeaderType', payload);
  },
  updateDialogLeftHeaderIcon({ commit }, payload) {
    commit('updateDialogLeftHeaderIcon', payload);
  },
  setDialogLeftHeaderIconType({ commit }, payload) {
    commit('setDialogLeftHeaderIconType', payload);
  },
  setDialogLeftHeaderIconComponent({ commit }, payload) {
    commit('setDialogLeftHeaderIconComponent', payload);
  },
  updateAttachments({ commit }, payload) {
    commit('updateAttachments', payload);
  },
  setReturnData({ commit }, payload) {
    commit('setReturnData', payload);
  },
  resetForm({ commit }, payload) {
    commit('resetResponses', payload);
    commit('resetValidations', payload);
    commit('resetAttachments', payload);
    commit('setDefaultFormData', payload);
    commit('updateLoading', {
      formName: payload,
      value: false
    });
  }
};

export const dynamicForms = {
  state,
  getters,
  mutations,
  actions,
  namespaced: true
};
